import { useState, useMemo, memo } from "react"
import { Box, Button, Flex, Heading, Input, Text, VStack, HStack, Badge, createListCollection } from "@chakra-ui/react"
import { SelectRoot, SelectTrigger, SelectValueText, SelectContent, SelectItem } from "@chakra-ui/react"
import { useCreatePromoCode } from "./hooks/useCreatePromoCode"
import { useDeletePromoCode } from "./hooks/useDeletePromoCode"
import { usePromoCodes } from "./hooks/usePromoCodes"
import { extractErrorInfo } from "../../lib/api/error-utils"
import ErrorAlert from "../../components/ErrorAlert"
import SectionCard from "../../framework/SectionCard"
import type { CreatePromoCodeResponse, PromoCodeListItem } from "./types"

const DOMAIN = "pistepal.app"
const ENTITLEMENT = "pistepal.annual"

const DURATION_OPTIONS = createListCollection({
  items: [
    { value: "365", label: "1 year" },
    { value: "1095", label: "3 years" },
    { value: "36500", label: "Lifetime" },
  ],
})

const formatDuration = (days: number): string => {
  if (days === 365) return "1 year"
  if (days === 1095) return "3 years"
  if (days >= 36500) return "Lifetime"
  return `${days} days`
}

export default function PromoCodes() {
  const { data: promoCodesData, isLoading: isLoadingPromoCodes } = usePromoCodes()
  
  const tableData = useMemo(() => promoCodesData?.PromoCodes || [], [promoCodesData])

  return (
    <Box p="6">
      <Heading size="2xl" mb="6">
        Promotional Codes
      </Heading>

      <PromoCodesForm />

      <Box mt="8">
        <PromoCodesTable data={tableData} isLoading={isLoadingPromoCodes} />
      </Box>
    </Box>
  )
}

const PromoCodesTable = memo(function PromoCodesTable({ 
  data, 
  isLoading 
}: { 
  data: PromoCodeListItem[]
  isLoading: boolean 
}) {
  const [deletingCode, setDeletingCode] = useState<string | null>(null)
  const [copiedTableCode, setCopiedTableCode] = useState<string | null>(null)
  const deleteMutation = useDeletePromoCode()

  const handleDelete = async (code: string) => {
    if (!confirm(`Are you sure you want to revoke promo code ${code}? This cannot be undone.`)) {
      return
    }

    setDeletingCode(code)
    try {
      await deleteMutation.mutateAsync(code)
    } catch (err) {
      const errorInfo = extractErrorInfo(err)
      alert(`Failed to revoke promo code: ${errorInfo.message}`)
    } finally {
      setDeletingCode(null)
    }
  }

  const handleCopyTableLink = (code: string) => {
    const link = `https://${DOMAIN}/promo?code=${code}`
    navigator.clipboard.writeText(link)
    setCopiedTableCode(code)
    setTimeout(() => setCopiedTableCode(null), 2000)
  }

  return (
    <SectionCard title="All Promo Codes">
      <Box overflowX="auto">
        {isLoading ? (
          <Flex align="center" justify="center" py="6">
            <Text fontSize="sm">Loading…</Text>
          </Flex>
        ) : data.length === 0 ? (
          <Text textAlign="center" fontSize="sm" color="gray.500" py="4">
            No promo codes created yet
          </Text>
        ) : (
          <Box as="table" width="100%">
            <Box as="thead">
              <Box as="tr">
                <Box as="th" fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px" textAlign="left">
                  Code
                </Box>
                <Box as="th" fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px" textAlign="left">
                  Notes
                </Box>
                <Box as="th" fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px" textAlign="left">
                  Duration
                </Box>
                <Box as="th" fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px" textAlign="left">
                  Created
                </Box>
                <Box as="th" fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px" textAlign="left">
                  Status
                </Box>
                <Box as="th" fontSize="xs" letterSpacing="wide" textTransform="uppercase" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px" textAlign="left">
                  Actions
                </Box>
              </Box>
            </Box>
            <Box as="tbody">
              {data.map((item) => (
                <Box as="tr" key={item.Code}>
                  <Box as="td" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    <Text fontFamily="mono" fontSize="sm" fontWeight="bold">
                      {item.Code}
                    </Text>
                  </Box>
                  <Box as="td" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    <Text fontSize="sm">{item.Notes}</Text>
                  </Box>
                  <Box as="td" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    <Text fontSize="sm">{formatDuration(item.DurationDays)}</Text>
                  </Box>
                  <Box as="td" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    <Text fontSize="sm" color="gray.400">
                      {item.CreatedAt}
                    </Text>
                  </Box>
                  <Box as="td" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    {item.UsedAt ? (
                      <Box>
                        <Badge colorPalette="red" size="sm" display="inline-block" mb="1">
                          Used {item.UsedAt}
                          {item.UsedByName && (
                            <Text fontSize="xs" color="gray.800" display="inline" ml="1">
                              by {item.UsedByName}
                            </Text>
                          )}
                        </Badge>
                      </Box>
                    ) : (
                      <Badge colorPalette="green" size="sm">
                        Available
                      </Badge>
                    )}
                  </Box>
                  <Box as="td" px="3" py="2" borderColor="gray.700" borderBottomWidth="1px">
                    <HStack gap="2">
                      {!item.UsedAt && (
                        <Button
                          size="xs"
                          variant="surface"
                          colorPalette={copiedTableCode === item.Code ? "green" : "gray"}
                          onClick={() => handleCopyTableLink(item.Code)}
                        >
                          {copiedTableCode === item.Code ? '✓ Copied' : 'Copy Link'}
                        </Button>
                      )}
                      {!item.UsedAt && (
                        <Button
                          size="xs"
                          colorPalette="red"
                          onClick={() => handleDelete(item.Code)}
                          loading={deletingCode === item.Code}
                          disabled={deletingCode !== null}
                        >
                          Revoke
                        </Button>
                      )}
                    </HStack>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </SectionCard>
  )
})

const PromoCodesForm = memo(function PromoCodesForm() {
  const [durationDays, setDurationDays] = useState("365")
  const [notes, setNotes] = useState("")
  const [error, setError] = useState<{ message: string; traceId?: string } | null>(null)
  const [createdCode, setCreatedCode] = useState<CreatePromoCodeResponse | null>(null)
  const [copiedItem, setCopiedItem] = useState<string | null>(null)

  const createMutation = useCreatePromoCode()

  const handleCreate = async () => {
    setError(null)
    setCreatedCode(null)

    const duration = parseInt(durationDays, 10)
    if (isNaN(duration) || duration < 1) {
      setError({ message: "Duration must be a positive number" })
      return
    }

    if (!notes.trim()) {
      setError({ message: "You have to say who it's for" })
      return
    }

    try {
      const result = await createMutation.mutateAsync({
        Entitlement: ENTITLEMENT,
        DurationDays: duration,
        Notes: notes.trim(),
      })
      setCreatedCode(result)
      // Reset form
      setDurationDays("365")
      setNotes("")
    } catch (err) {
      const errorInfo = extractErrorInfo(err)
      setError({
        message: errorInfo.message,
        traceId: errorInfo.traceId ?? undefined
      })
    }
  }

  const handleCopyCode = () => {
    if (createdCode) {
      navigator.clipboard.writeText(createdCode.Code)
      setCopiedItem('code')
      setTimeout(() => setCopiedItem(null), 2000)
    }
  }

  const handleCopyLink = () => {
    if (createdCode) {
      navigator.clipboard.writeText(createdCode.RedeemLink)
      setCopiedItem('link')
      setTimeout(() => setCopiedItem(null), 2000)
    }
  }

  return (
    <Flex gap="6" direction={{ base: "column", lg: "row" }}>
      {/* Create Form */}
      <Box flex="1">
          <SectionCard title="Create Promo Code">
            <VStack gap="4" align="stretch">
              <Box>
                <Text fontSize="sm" mb="2" color="gray.300">
                  Entitlement
                </Text>
                <Input
                  value={ENTITLEMENT}
                  readOnly
                  bg="gray.700"
                  disabled
                />
              </Box>

              <Box>
                <Text fontSize="sm" mb="2" color="gray.300">
                  Duration
                </Text>
                <SelectRoot
                  collection={DURATION_OPTIONS}
                  value={[durationDays]}
                  onValueChange={(e) => setDurationDays(e.value[0])}
                  disabled={createMutation.isPending}
                  size="sm"
                >
                  <SelectTrigger bg="gray.700">
                    <SelectValueText placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent bg="gray.800" borderColor="gray.600">
                    {DURATION_OPTIONS.items.map((option) => (
                      <SelectItem key={option.value} item={option} _hover={{ bg: "gray.700" }}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              </Box>

              <Box>
                <Text fontSize="sm" mb="2" color="gray.300">
                  Notes (who's it for?)
                </Text>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., Marketing campaign Q1 2025"
                  bg="gray.700"
                  disabled={createMutation.isPending}
                />
              </Box>

              <Button
                size="sm"
                colorPalette="blue"
                onClick={handleCreate}
                loading={createMutation.isPending}
              >
                Create Promo Code
              </Button>

              {error && <ErrorAlert message={error.message} traceId={error.traceId} />}
            </VStack>
          </SectionCard>
        </Box>

        {/* Result Display */}
        <Box flex="1">
          <SectionCard title="Generated Code">
            {createdCode ? (
              <VStack gap="4" align="stretch">
                <Box>
                  <Text fontSize="sm" mb="2" color="gray.400">
                    Promo Code
                  </Text>
                  <HStack>
                    <Input
                      value={createdCode.Code}
                      readOnly
                      bg="gray.700"
                      color="white"
                      fontFamily="mono"
                      fontSize="lg"
                      fontWeight="bold"
                    />
                    <Button
                      onClick={handleCopyCode}
                      size="sm"
                      variant="surface"
                      colorPalette={copiedItem === 'code' ? "green" : "gray"}
                    >
                      {copiedItem === 'code' ? '✓ Copied' : 'Copy'}
                    </Button>
                  </HStack>
                </Box>

                <Box>
                  <Text fontSize="sm" mb="2" color="gray.400">
                    Deep Link
                  </Text>
                  <HStack>
                    <Input
                      value={createdCode.RedeemLink}
                      readOnly
                      bg="gray.700"
                      color="white"
                      fontFamily="mono"
                      fontSize="sm"
                    />
                    <Button
                      onClick={handleCopyLink}
                      size="sm"
                      variant="surface"
                      colorPalette={copiedItem === 'link' ? "green" : "gray"}
                    >
                      {copiedItem === 'link' ? '✓ Copied' : 'Copy'}
                    </Button>
                  </HStack>
                </Box>

                <Box>
                  <Text fontSize="sm" mb="2" color="gray.400">
                    Details
                  </Text>
                  <VStack align="stretch" gap="2" bg="gray.700" p="3" borderRadius="md">
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.400">
                        Entitlement:
                      </Text>
                      <Badge colorPalette="blue">{createdCode.Entitlement}</Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.400">
                        Duration:
                      </Text>
                      <Text fontSize="sm" fontWeight="medium" color="white">
                        {formatDuration(createdCode.DurationDays)}
                      </Text>
                    </HStack>
                    <Box>
                      <Text fontSize="sm" color="gray.400" mb="1">
                        Notes:
                      </Text>
                      <Text fontSize="sm" color="white">{createdCode.Notes}</Text>
                    </Box>
                  </VStack>
                </Box>

                <Box bg="blue.900" p="3" borderRadius="md" borderWidth="1px" borderColor="blue.700">
                  <Text fontSize="sm" color="blue.200">
                    This code can be redeemed once by any user. Share the deep link or code with the recipient.
                  </Text>
                </Box>
              </VStack>
            ) : (
              <Text color="gray.500" textAlign="center" py="8">
                Create a promo code to see the result here
              </Text>
            )}
          </SectionCard>
        </Box>
      </Flex>
  )
})
